import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { fetchWithAuth } from '../services/api';

const QrClaimPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const [qrCode, setQrCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [userData, setUserData] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        introduction: '',
        lifeStory: '',
        birthDate: '',
        birthPlace: '',
        deathDate: '',
        deathPlace: '',
        restingPlaceName: '',
        restingPlaceCity: '',
        restingPlaceCountry: '',
        coordinates: '',
        defaultLanguage: ''
    });

    useEffect(() => {
        // Get QR code from navigation state, URL params, or session storage
        const qrFromState = location.state?.qrCode;
        const qrFromParams = searchParams.get('qr');
        const qrFromStorage = sessionStorage.getItem('qrCodeToClaim');
        
        if (qrFromState) {
            setQrCode(qrFromState);
            sessionStorage.setItem('qrCodeToClaim', qrFromState);
        } else if (qrFromParams) {
            setQrCode(qrFromParams);
            sessionStorage.setItem('qrCodeToClaim', qrFromParams);
        } else if (qrFromStorage) {
            setQrCode(qrFromStorage);
        } else {
            // No QR code to claim, redirect to home
            navigate(`/${i18n.language}/`);
        }
    }, [location.state, searchParams, navigate, i18n.language]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await fetchWithAuth('/users/me');
                setUserData(response);
                
                // Set user's default language
                if (response.defaultLanguage) {
                    setFormData(prevData => ({
                        ...prevData,
                        defaultLanguage: response.defaultLanguage
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch user data:', error);
            }
        };

        fetchUserData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) {
            setMessage({
                type: 'error',
                text: t('qrClaim.nameRequired', 'Profile name is required.')
            });
            return;
        }

        if (!formData.defaultLanguage.trim()) {
            setMessage({
                type: 'error',
                text: t('qrClaim.languageRequired', 'Default language is required.')
            });
            return;
        }

        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            // Create profile (QR code will be auto-assigned)
            const response = await fetchWithAuth('/api/profiles', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const profileData = await response.json();
                
                // Clear QR code from session storage
                sessionStorage.removeItem('qrCodeToClaim');
                
                setMessage({
                    type: 'success',
                    text: t('qrClaim.successMessage', `Profile created successfully and linked to QR code ${qrCode}!`)
                });
                
                // Redirect to the new profile after a short delay
                setTimeout(() => {
                    navigate(`/${i18n.language}/profile/${profileData.stringId}`);
                }, 2000);
                
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to create profile');
            }
        } catch (error) {
            console.error('Error creating profile:', error);
            setMessage({
                type: 'error',
                text: t('qrClaim.errorMessage', error.message || 'Failed to create profile. Please try again.')
            });
        } finally {
            setLoading(false);
        }
    };

    if (!qrCode) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6" data-testid="qr-claim-page">
            <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="page-title">
                        {t('qrClaim.title', 'Claim QR Code')}
                    </h1>
                    <p className="text-lg text-gray-600 mb-4">
                        {t('qrClaim.subtitle', `You're claiming QR code: ${qrCode}`)}
                    </p>
                    <div className="inline-block bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                        <span className="font-mono text-lg font-bold text-blue-800">{qrCode}</span>
                    </div>
                </div>

                {/* Status Message */}
                {message.text && (
                    <div className={`mb-6 p-4 rounded-md ${
                        message.type === 'success' 
                            ? 'bg-green-50 border border-green-200 text-green-800' 
                            : 'bg-red-50 border border-red-200 text-red-800'
                    }`} data-testid="status-message">
                        {message.text}
                    </div>
                )}

                {/* Instructions */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-8">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                                {t('qrClaim.instructionsTitle', 'Create a Memorial Profile')}
                            </h3>
                            <p className="mt-1 text-sm text-yellow-700">
                                {t('qrClaim.instructions', 'Fill out the form below to create a memorial profile that will be linked to this QR code. Once created, anyone scanning this QR code will be directed to this memorial.')}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Profile Creation Form */}
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="profile-form">
                    {/* Basic Information */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('qrClaim.basicInfo', 'Basic Information')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.name', 'Full Name')} *
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    data-testid="name-input"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.namePlaceholder', 'Enter the full name...')}
                                />
                            </div>

                            <div>
                                <label htmlFor="introduction" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.introduction', 'Brief Introduction')}
                                </label>
                                <input
                                    type="text"
                                    id="introduction"
                                    name="introduction"
                                    data-testid="introduction-input"
                                    value={formData.introduction}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.introductionPlaceholder', 'A brief introduction...')}
                                />
                            </div>
                            
                            <div className="col-span-1 md:col-span-2">
                                <label htmlFor="defaultLanguage" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('profileCreator.fields.defaultLanguage.label', 'Default Language')} *
                                </label>
                                <select
                                    id="defaultLanguage"
                                    name="defaultLanguage"
                                    data-testid="defaultlanguage-select"
                                    value={formData.defaultLanguage}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="">{t('profileCreator.fields.defaultLanguage.placeholder', 'Select Language')}</option>
                                    <option value="MACEDONIAN">{t('languages.macedonian', 'Macedonian')}</option>
                                    <option value="ALBANIAN">{t('languages.albanian', 'Albanian')}</option>
                                    <option value="TURKISH">{t('languages.turkish', 'Turkish')}</option>
                                    <option value="ENGLISH">{t('languages.english', 'English')}</option>
                                    <option value="GERMAN">{t('languages.german', 'German')}</option>
                                    <option value="SERBIAN">{t('languages.serbian', 'Serbian')}</option>
                                    <option value="CROATIAN">{t('languages.croatian', 'Croatian')}</option>
                                </select>
                                <p className="mt-1 text-sm text-gray-600">
                                    {t('profileCreator.fields.defaultLanguage.help', 'Choose the primary language for this memorial profile')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Life Dates */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('qrClaim.lifeDates', 'Life Dates')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.birthDate', 'Birth Date')}
                                </label>
                                <input
                                    type="text"
                                    id="birthDate"
                                    name="birthDate"
                                    data-testid="birth-date-input"
                                    value={formData.birthDate}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="DD.MM.YYYY"
                                />
                            </div>

                            <div>
                                <label htmlFor="birthPlace" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.birthPlace', 'Birth Place')}
                                </label>
                                <input
                                    type="text"
                                    id="birthPlace"
                                    name="birthPlace"
                                    data-testid="birth-place-input"
                                    value={formData.birthPlace}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.birthPlacePlaceholder', 'City, Country')}
                                />
                            </div>

                            <div>
                                <label htmlFor="deathDate" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.deathDate', 'Death Date')}
                                </label>
                                <input
                                    type="text"
                                    id="deathDate"
                                    name="deathDate"
                                    data-testid="death-date-input"
                                    value={formData.deathDate}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="DD.MM.YYYY"
                                />
                            </div>

                            <div>
                                <label htmlFor="deathPlace" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.deathPlace', 'Death Place')}
                                </label>
                                <input
                                    type="text"
                                    id="deathPlace"
                                    name="deathPlace"
                                    data-testid="death-place-input"
                                    value={formData.deathPlace}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.deathPlacePlaceholder', 'City, Country')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Life Story */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('qrClaim.lifeStoryTitle', 'Life Story')}
                        </h3>
                        <textarea
                            id="lifeStory"
                            name="lifeStory"
                            data-testid="life-story-input"
                            value={formData.lifeStory}
                            onChange={handleInputChange}
                            rows={4}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t('qrClaim.lifeStoryPlaceholder', 'Share memories, accomplishments, or anything that celebrates their life...')}
                        />
                    </div>

                    {/* Resting Place */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {t('qrClaim.restingPlace', 'Resting Place')}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="restingPlaceName" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.restingPlaceName', 'Cemetery/Place Name')}
                                </label>
                                <input
                                    type="text"
                                    id="restingPlaceName"
                                    name="restingPlaceName"
                                    data-testid="resting-place-name-input"
                                    value={formData.restingPlaceName}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.restingPlaceNamePlaceholder', 'Cemetery name...')}
                                />
                            </div>

                            <div>
                                <label htmlFor="restingPlaceCity" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.restingPlaceCity', 'City')}
                                </label>
                                <input
                                    type="text"
                                    id="restingPlaceCity"
                                    name="restingPlaceCity"
                                    data-testid="resting-place-city-input"
                                    value={formData.restingPlaceCity}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.restingPlaceCityPlaceholder', 'City name...')}
                                />
                            </div>

                            <div>
                                <label htmlFor="restingPlaceCountry" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.restingPlaceCountry', 'Country')}
                                </label>
                                <input
                                    type="text"
                                    id="restingPlaceCountry"
                                    name="restingPlaceCountry"
                                    data-testid="resting-place-country-input"
                                    value={formData.restingPlaceCountry}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('qrClaim.restingPlaceCountryPlaceholder', 'Country name...')}
                                />
                            </div>

                            <div>
                                <label htmlFor="coordinates" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('qrClaim.coordinates', 'GPS Coordinates (Optional)')}
                                </label>
                                <input
                                    type="text"
                                    id="coordinates"
                                    name="coordinates"
                                    data-testid="coordinates-input"
                                    value={formData.coordinates}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="41.9981, 21.4254"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center pt-6">
                        <button
                            type="submit"
                            disabled={loading || !formData.name.trim()}
                            className={`px-8 py-3 rounded-md font-medium transition-colors ${
                                loading || !formData.name.trim()
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                            }`}
                            data-testid="create-profile-button"
                        >
                            {loading ? (
                                <span className="flex items-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    {t('qrClaim.creating', 'Creating Profile...')}
                                </span>
                            ) : (
                                t('qrClaim.createButton', 'Create Memorial Profile')
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default QrClaimPage; 